import { Fragment } from 'react';

import { Button, ButtonLink } from '@poseidon/components/Input2';
import { StepIcon } from '@poseidon/components/StepIcon';
import { Arrows, General } from '@poseidon/components/StrokeIcons';
import type CadenceStepType from '@poseidon/types/pg-gen/cadenceStepType';
import { formatAum } from '@poseidon/shared-utils';

import { type SwitchBranchDraft } from './SwitchBranchBuilder';

interface SwitchBranchesViewProps {
  branches: SwitchBranchDraft[];
  /** Maps a template public id → its display name for the destination column. */
  templateNameById: Map<string, string>;
  /** Maps a template public id → its ordered action-step types for a compact preview. */
  templateStepsById: Map<string, CadenceStepType[]>;
  unit: string | null;
  /** Routing attribute shown in the header inequality, e.g. "age" in "40 ≤ age < 65". */
  attributeLabel: string | null;
  onEdit: () => void;
}

/** Units rendered as money: a `$` prefix + thousands separators instead of a trailing unit. */
const CURRENCY_UNITS = new Set(['USD']);

/** True when a switch attribute's unit is a currency (e.g. AUM's "USD"). */
export function isCurrencyUnit(unit: string | null): boolean {
  return unit != null && CURRENCY_UNITS.has(unit);
}

/**
 * Format a single range bound for display. Currency units (e.g. AUM) render in
 * compact human form — `$250K`, `$200M`, `$1.5B` — via {@link formatAum}, so a
 * header reads `$1 ≤ ~aum < $200M` instead of `$1 ≤ ~aum < $200,000,000`.
 * Everything else is the raw value.
 */
export function formatRangeBound(value: string, unit: string | null): string {
  const trimmed = value.trim();
  if (!isCurrencyUnit(unit)) return trimmed;
  const n = Number(trimmed);
  if (trimmed === '' || Number.isNaN(n)) return trimmed;
  return formatAum(n);
}

/**
 * Normalize typed text to a raw integer string: digits only, no thousands separators,
 * no leading zeros (a lone "0" is kept). "1,000,000" → "1000000", "007" → "7", "" → "".
 * String-based so arbitrarily large values (billions/trillions) keep every digit.
 */
export function parseThousands(input: string): string {
  return input.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
}

/**
 * Group a raw integer string with thousands separators for display in an input:
 * "1000000" → "1,000,000", "" → "". No currency symbol (the `$` is a separate adornment).
 */
export function formatThousands(value: string): string {
  const digits = parseThousands(value);
  if (digits === '') return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Half-open [min, max) range rendered as an inequality section header:
 * "< 40 years", "40 ≤ age < 65", "≥ 65 years", "Any". The lower bound is
 * inclusive and the upper bound exclusive — matching how the switch resolves.
 * Currency attributes (e.g. AUM) format bounds compactly as "$250K" / "$200M"
 * and drop the unit suffix, since the `$` already conveys it.
 */
export function formatRangeHeader(
  min: string,
  max: string,
  unit: string | null,
  attributeLabel: string | null,
): string {
  const hasMin = min.trim() !== '';
  const hasMax = max.trim() !== '';
  // Currency is self-describing via the `$` prefix, so it carries no unit suffix.
  const unitSuffix = unit && !isCurrencyUnit(unit) ? ` ${unit}` : '';
  const attr = (attributeLabel ?? 'value').toLowerCase();
  const minLabel = formatRangeBound(min, unit);
  const maxLabel = formatRangeBound(max, unit);
  if (hasMin && hasMax) return `${minLabel} ≤ ${attr} < ${maxLabel}`;
  if (hasMax) return `< ${maxLabel}${unitSuffix}`;
  if (hasMin) return `≥ ${minLabel}${unitSuffix}`;
  return `Any${unitSuffix}`;
}

/**
 * Read-only summary of a switch's configured ranges, laid out as stacked
 * sections: each range is its own card (inequality header + the campaigns a
 * matching contact fans out into, each with a compact step preview). Mirrors
 * `FilterConditionsView` in StagingFilterRulesEditor for the empty state.
 */
export function SwitchBranchesView({
  branches,
  templateNameById,
  templateStepsById,
  unit,
  attributeLabel,
  onEdit,
}: SwitchBranchesViewProps) {
  if (branches.length === 0) {
    return (
      <div className="flex flex-col items-stretch gap-2">
        <div className="rounded-md border border-dashed border-lynch-300 px-3 py-4 text-center text-sm italic text-lynch-600 dark:border-slate-700 dark:text-lynch-400">
          No ranges yet — every researched contact will be errored out until you add one.
        </div>
        <Button
          type="button"
          color="blue"
          tone="soft"
          size="sm"
          onClick={onEdit}
          icon={<General.Plus />}
          textAlign="center"
          className="w-full justify-center">
          Add a range
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {branches.map((branch) => (
        <section
          key={branch.id}
          className="overflow-hidden rounded-md border border-lynch-200 dark:border-slate-700">
          <header className="border-b border-lynch-200 bg-lynch-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/50">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {formatRangeHeader(branch.min, branch.max, unit, attributeLabel)}
            </span>
          </header>

          <div className="flex flex-col gap-2 p-3">
            {branch.cadenceTemplateIds.length === 0 && (
              <span className="text-sm italic text-lynch-500 dark:text-lynch-400">No campaigns</span>
            )}

            {branch.cadenceTemplateIds.map((templateId) => {
              const steps = templateStepsById.get(templateId) ?? [];
              return (
                <div key={templateId} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <ButtonLink
                    size="2xs"
                    color="blue"
                    tone="soft"
                    href={`/templates/${templateId}`}
                    tooltip="Open campaign">
                    {templateNameById.get(templateId) ?? 'Unknown campaign'}
                  </ButtonLink>

                  {steps.length === 0 && (
                    <span className="text-xs italic text-lynch-400 dark:text-lynch-500">No steps yet</span>
                  )}
                  {steps.length > 0 && (
                    <div className="flex items-center gap-1 text-lynch-500 dark:text-lynch-400">
                      {steps.map((type, i) => (
                        <Fragment key={`${type}-${i}`}>
                          {i > 0 && <Arrows.ChevronRight className="size-2.5 shrink-0" />}
                          <StepIcon iconType={type} className="size-3.5" />
                        </Fragment>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
